import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

import { LogoDark } from "../../assets/img/logo/logoDark";
import { LogoLight } from "../../assets/img/logo/logoLight";
import { ArrowDown } from "../../ontego-ui/sap_icons";
import MegaWarehouse from "../../Icons/MegaWarehouse";
import MegaMobileSearch from "../../Icons/MegaMobileSearch";
import MegaFilesMessage from "../../Icons/MegaFilesMessage";
import MegaHeadphone from "../../Icons/MegaHeadphone";
import ArrowHeadSpecial from "../../Icons/ArrowHeadSpecial";

import Styled from "./SAPTopNavStyles";
import Typography from "../../ontego-ui/components/Typography";
import { ColorSAP, FONT_FAMILY } from "../../ontego-ui";
import { height, lineHeight } from "@mui/system";
import Button from "../Button/Button";

const RESOURCE_CARDS = [
  {
    Icon: MegaWarehouse,
    titleKey: "navbar.solutions",
    descKey: "navbar.solutionsExp",
    url: "https://ontego.de#solutions",
  },
  {
    Icon: MegaMobileSearch,
    titleKey: "navbar.hardwareFinder",
    descKey: "navbar.hardwareFinderExp",
    url: "https://www.ontego.de/sap_mobility/mobile-geraete.html",
  },
  {
    Icon: MegaFilesMessage,
    titleKey: "navbar.mobilityBlog",
    descKey: "navbar.mobilityBlogExp",
    url: "https://blog.ontego.de",
  },
  {
    Icon: MegaHeadphone,
    titleKey: "navbar.supportPortal",
    descKey: "navbar.supportPortalExp",
    url: "https://support.commsult.de",
  },
];

const HOVER_CLOSE_DELAY = 200;

export const SAPTopNav = (props) => {
  const { t, i18n } = useTranslation();
  const {
    onLogoClick,
    onProcessesClick,
    onManagementClick,
    onLogoutClick,
    onChangeLanguageClick,
    userInfo,
    variant = "light",
  } = props;

  const isDark = variant === "dark";

  const [isScrolled, setIsScrolled] = useState(isDark ? true : false);
  const [isPillHidden, setIsPillHidden] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNavHovered, setIsNavHovered] = useState(false);
  const timeoutRef = useRef(null);
  const navHoverRef = useRef(null);

  useEffect(() => {
    if (isDark) {
      return;
    }

    const handleScroll = () => {
      const y = window.scrollY;
      const hidden = y > 108;
      const scrolled = y > 520;
      setIsPillHidden(hidden);
      setIsScrolled(scrolled);
      if (hidden && !scrolled) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDark]);

  const openDropdown = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  }, []);

  const scheduleClose = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
      timeoutRef.current = null;
    }, HOVER_CLOSE_DELAY);
  }, []);

  const cancelClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleNavEnter = useCallback(() => {
    if (navHoverRef.current) {
      clearTimeout(navHoverRef.current);
      navHoverRef.current = null;
    }
    setIsNavHovered(true);
  }, []);

  const handleNavLeave = useCallback(() => {
    navHoverRef.current = setTimeout(() => {
      setIsNavHovered(false);
      navHoverRef.current = null;
    }, HOVER_CLOSE_DELAY);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (navHoverRef.current) {
        clearTimeout(navHoverRef.current);
      }
    };
  }, []);

  return (
    <Styled.Container isScrolled={isScrolled} isDark={isDark}>
      {/* Navigation Bar */}
      <Styled.NavSection
        isScrolled={isScrolled}
        isPillHidden={isPillHidden}
        isDark={isDark}
        onMouseEnter={handleNavEnter}
        onMouseLeave={handleNavLeave}
      >
        <Styled.NavPill
          isScrolled={isScrolled}
          isPillHidden={isPillHidden}
          isDark={isDark}
        >
          <Styled.Logo onClick={onLogoClick}>
            {isDark ? <LogoLight /> : <LogoDark />}
          </Styled.Logo>

          <Styled.Menus>
            <Styled.MenuText isDark={isDark} onClick={onProcessesClick}>
              {t("navbar.processes")}
            </Styled.MenuText>

            <Styled.ResourcesWrapper
              isDark={isDark}
              onMouseEnter={openDropdown}
              onMouseLeave={scheduleClose}
            >
              <Styled.ResourcesText isDark={isDark}>
                {t("navbar.resources")}
              </Styled.ResourcesText>
              <Styled.ArrowIcon isOpen={isDropdownOpen} isDark={isDark}>
                <ArrowDown />
              </Styled.ArrowIcon>
            </Styled.ResourcesWrapper>

            {userInfo && userInfo.role !== "PROSPECT" && (
              <Styled.MenuText isDark={isDark} onClick={onManagementClick}>
                {t("navbar.management")}
              </Styled.MenuText>
            )}

            <Styled.LangSwitcher>
              <Styled.LangButton
                isDark={isDark}
                isActive={i18n.language === "de"}
                position="left"
                onClick={() => onChangeLanguageClick("de")}
              >
                {`DE`}
              </Styled.LangButton>
              <Styled.LangButton
                isDark={isDark}
                isActive={i18n.language === "en"}
                position="middle"
                onClick={() => onChangeLanguageClick("en")}
              >
                {`EN`}
              </Styled.LangButton>
              <Styled.LangButton
                isDark={isDark}
                isActive={i18n.language === "id"}
                position="right"
                onClick={() => onChangeLanguageClick("id")}
              >
                {`ID`}
              </Styled.LangButton>
            </Styled.LangSwitcher>

            <Button
              type="primary"
              theme={isDark ? "light" : "dark"}
              size="small"
              onClick={onLogoutClick}
            >
              {t("button.logout").toUpperCase()}
            </Button>
          </Styled.Menus>
        </Styled.NavPill>
      </Styled.NavSection>

      {/* Mega Dropdown */}
      <Styled.DropdownWrapper
        isVisible={
          isDropdownOpen &&
          !(isPillHidden && !isScrolled) &&
          (isScrolled || isNavHovered)
        }
      >
        {!isScrolled && (
          <Styled.HoverBridge
            onMouseEnter={() => {
              cancelClose();
              handleNavEnter();
            }}
            onMouseLeave={() => {
              scheduleClose();
              handleNavLeave();
            }}
          />
        )}
        <Styled.MegaDropdown
          isScrolled={isScrolled}
          onMouseEnter={() => {
            cancelClose();
            handleNavEnter();
          }}
          onMouseLeave={() => {
            scheduleClose();
            handleNavLeave();
          }}
        >
          <div style={{ width: "1200px", display: "flex", gap: "24px" }}>
            {RESOURCE_CARDS.map((card) => (
              <Styled.Card
                key={card.titleKey}
                href={card.url}
                target="_blank"
                rel="noreferrer"
                isScrolled={isScrolled}
              >
                <Styled.CardIcon className="card-icon">
                  <card.Icon />
                </Styled.CardIcon>
                <Styled.CardContent>
                  <Styled.CardText>
                    <Typography
                      fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_SEMIBOLD}
                      size={18}
                      color={ColorSAP.neutral[100]}
                      style={{ lineHeight: "22px" }}
                    >
                      {t(card.titleKey)}
                    </Typography>

                    <Typography
                      fontFamily={FONT_FAMILY.PLUS_JAKARTA_SANS_REGULAR}
                      size={14}
                      color={ColorSAP.neutral[100]}
                      style={{ lineHeight: "20px" }}
                    >
                      {t(card.descKey)}
                    </Typography>
                  </Styled.CardText>
                  <Styled.CardLearnMore>
                    <Styled.CardLearnMoreText>
                      {t("navbar.learnMore")}
                    </Styled.CardLearnMoreText>
                    <Styled.CardLearnMoreArrow>
                      <ArrowHeadSpecial />
                    </Styled.CardLearnMoreArrow>
                  </Styled.CardLearnMore>
                </Styled.CardContent>
              </Styled.Card>
            ))}
          </div>
        </Styled.MegaDropdown>
      </Styled.DropdownWrapper>
    </Styled.Container>
  );
};

export default React.memo(SAPTopNav);
